import * as React from 'react'
import { StoreProps, castingStoreCanFocus, castingStoreGetDurationForAllowedPattern, castingStoreGetFirstEnabledDateFrom, castingStoreGetMaxDuration, castingStoreGetMinDuration, castingStoreGetMinStartDate, castingStoreGetMultiOpeningHours, castingStoreIsAllowingAnyTime, castingStoreIsComplex, castingStoreIsStartDayDisabled } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { formatDurationFromSecs } from '../../../../services/utils/utils.service'
import StepperInputComponent from '../../../shared/stepper-input.component'
import { openableGetAvailableAsapOffers, openableReduce } from '../../../../services/utils/openables.service'
import { moment } from '../../../../services/utils/moment.service'
import { FloatingContainerTitle } from '../casting-styled.component'
import { castingExtendsAllowedPatternOnDurationChanged } from '../../../../services/models/castings.service'
import CastingStartDateMultipleDaysSlotComponent from './casting-start-date-multiple-days-slot.component'
import CastingStartDateAsapComponent from './casting-start-date-asap.component'
import CastingStartDateShorterSlotsComponent from './casting-start-date-shorter-slots.component'
import CastingStartDateLodgingComponent from './casting-start-date-lodging.component'

type Props = StoreProps & { showNextStepIfTimeUnitIsDay?: boolean }
const i18nScoped = new I18nService(require('./casting-start-date-picker.component.yml'))

export default function CastingStartDatePickerComponent ({ store, data, data: { castingType, offeredService: os, slots, enableLodging } }: Props) {
  const { multiOpeningHours, multiOpeningHoursUnsafeMidnight } = React.useMemo(() => {
    /**
     * Is worthy true if the allowed_patterns are frozen (set in previous step and not updated in current component).
     * This is the case, for example, when selecting the start date of a simple recurrence (the allowed patterns are set in frequency step and we just pick a start date here).
     */
    const areAllowedPatternFrozen = castingType === 'casting_recurring' &&
      !castingStoreIsComplex(data) &&
      os.rentable_unit_of_work !== 'day' &&
      !os.no_start_time_nor_duration_asked_to_customer

    return castingStoreGetMultiOpeningHours(data, { areAllowedPatternFrozen })
  }, [castingType, data, os.no_start_time_nor_duration_asked_to_customer, os.rentable_unit_of_work])

  /** Returns true if no slot can start at that day. */
  const isStartDayDisabled = React.useCallback((date: Date, opts: { duration?: number } = {}) => {
    return castingStoreIsStartDayDisabled(data, store, date, multiOpeningHours, opts)
  }, [data, multiOpeningHours, store])

  const renderAsap = os.asap && castingType === 'casting_one_shot'
  const flexibleStartDateInAbsenceOfStartTimeAndDuration = os.no_start_time_nor_duration_asked_to_customer && os.flexible_start_date_in_absence_of_start_time_and_duration
  const isAllowingAnyTime = React.useMemo(() => !flexibleStartDateInAbsenceOfStartTimeAndDuration && castingType === 'casting_one_shot' && castingStoreIsAllowingAnyTime(data), [castingType, data, flexibleStartDateInAbsenceOfStartTimeAndDuration])
  const startDate = React.useMemo(() => !isAllowingAnyTime && slots[0].start_date ? moment(slots[0].start_date).toDate() : null, [isAllowingAnyTime, slots])
  const fromMonth = React.useMemo(() => castingStoreGetFirstEnabledDateFrom(castingStoreGetMinStartDate(data).toDate(), isStartDayDisabled), [data, isStartDayDisabled])

  const showDurationSelector = React.useMemo(() => {
    // 'duration' step not focusable means calculator available.
    return !castingStoreCanFocus(data, 'duration') &&
      !castingStoreIsComplex(data) &&
      (os.rentable_unit_of_work === 'hour' || os.rentable_unit_of_work == 'casting') &&
      !(os['customer_choose_end_date?'] && castingType === 'casting_one_shot') &&
      !os.no_start_time_nor_duration_asked_to_customer
  }, [castingType, data, os])

  const availableAsapOffers = React.useMemo(() => {
    return openableGetAvailableAsapOffers(
      openableReduce(multiOpeningHoursUnsafeMidnight),
      os.asap_offers,
      castingStoreGetDurationForAllowedPattern(data) / 3600,
      os.duration_step / 3600
    )
  }, [data, multiOpeningHoursUnsafeMidnight, os.asap_offers, os.duration_step])

  const initialMonth = React.useMemo(() => {
    return startDate
      ? castingStoreGetFirstEnabledDateFrom(startDate, isStartDayDisabled)
      : fromMonth < new Date()
      ? new Date()
      : fromMonth
  }, [fromMonth, isStartDayDisabled, startDate])

  return <React.Fragment>
    { /** render a stepped input for the number of employees, that multiplies the slot duration (multiplier is applyed in toJson()) */ }
    { os.customer_asks_for_many_employees && <div style={{ marginBottom: showDurationSelector ? '0.5em' : '1.25em' }}>
      <FloatingContainerTitle>{ i18nScoped.t(showDurationSelector ? 'employees_count_and_duration' : 'employees_count') }</FloatingContainerTitle>

      <StepperInputComponent
        name="employeesCount"
        value={ slots[0].employees_count ?? 1 }
        onChange={ employeesCount => store.setSlots(slots.map(slot => ({ ...slot, employees_count: employeesCount }))) }
        min={ os.customer_asks_for_many_employees_min }
        max={ os.customer_asks_for_many_employees_max }
        step={ 1 }
        formatter={ val => i18nScoped.t('employees_count_formatted', { count: val }) } />
    </div> }

    { /** render a stepped input to increase or decrease event duration */ }
    { showDurationSelector && <div style={{ marginBottom: '1.25em' }}>
      { !os.customer_asks_for_many_employees && <FloatingContainerTitle>{ i18nScoped.t('duration') }</FloatingContainerTitle> }

      <StepperInputComponent
        name='duration' 
        value={ slots[0].duration }
        onChange={ duration => store.setSlots(castingExtendsAllowedPatternOnDurationChanged(slots, duration - slots[0].duration)) } 
        min={ castingStoreGetMinDuration(data) }
        max={ castingStoreGetMaxDuration(data) }
        step={ os.duration_step }
        formatter={ formatDurationFromSecs } />
    </div> }

    { /** Render asap UI */ }
    { renderAsap && <CastingStartDateAsapComponent
      store={ store }
      data={ data }
      availableAsapOffers={ availableAsapOffers }
      multiOpeningHoursUnsafeMidnight={ multiOpeningHoursUnsafeMidnight } /> }

    { (() => {
      // Render UI to select an event on multiple days
      if (os.rentable_unit_of_work === 'day') {
        return <CastingStartDateMultipleDaysSlotComponent
          store={ store }
          data={ data }
          fromMonth={ fromMonth }
          initialMonth={ initialMonth }
          startDate={ startDate }
          isStartDayDisabled={ isStartDayDisabled } />
      }

      if (enableLodging) {
        return <CastingStartDateLodgingComponent
          store={ store }
          data={ data }
          fromMonth={ fromMonth }
          initialMonth={ initialMonth }
          startDate={ startDate }
          isStartDayDisabled={ isStartDayDisabled } />
      }

      // Render 'start date picker' + 'start time picker' + optionally 'end time picker' (depending on os['customer_choose_end_date?']) when each slots last less than 24h.
      // In case of asap, two mode exists: asap only or asap with fallback to farer date if business is closed (availableAsapOffers empty).
      if (!renderAsap || (!os.asap_only && !availableAsapOffers.length)) {
        return <CastingStartDateShorterSlotsComponent
          store={ store }
          data={ data }
          initialMonth={ initialMonth }
          fromMonth={ fromMonth }
          startDate={ startDate }
          multiOpeningHours={ multiOpeningHours }
          isAllowingAnyTime={ isAllowingAnyTime }
          isStartDayDisabled={ isStartDayDisabled } />
      }

      return null
    })() }
  </React.Fragment>
}
